import React, { createRef } from 'react';
import axios from 'axios';
import { TcsHeaders } from '@tcs/common/http/tcs-headers';
import { Link, LinkProps } from '@forgeglobal/design-framework';
import { getOktaAuth } from '@tcs/client-portal-common/providers/SecurityProvider';

interface Props extends LinkProps {
  url: string;
  defaultFileName: string;
  children: React.ReactNode;
  openInNewTab: boolean;
}

const normalizeHeaderValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.find((item) => typeof item === 'string') ?? null;
  }

  return null;
};

export function AuthenticatedLink({
  url,
  defaultFileName,
  children,
  openInNewTab,
  ...other
}: Props) {
  const link = createRef<HTMLAnchorElement>();

  const handleAction = async (event) => {
    if (link.current.href) {
      if (openInNewTab) {
        window.open(link.current.href, '_blank');
        event.preventDefault();
      }
      return;
    }

    const {
      accessToken: { accessToken },
    } = await getOktaAuth().tokenManager.getTokens();

    const response = await axios.get(url, {
      responseType: 'arraybuffer',
      headers: {
        [TcsHeaders.Authorization]: accessToken,
      },
    });
    const contentType = normalizeHeaderValue(response.headers['content-type']);
    const blob = new Blob([response.data], {
      type: contentType ?? undefined,
    });
    const href = window.URL.createObjectURL(blob);

    const contentDisposition = normalizeHeaderValue(
      response.headers['content-disposition'],
    );
    const fileName = contentDisposition
      ?.split('; ')
      .find((disposition) => disposition.startsWith('filename='))
      ?.substring('filename='.length);

    link.current.download = fileName ?? defaultFileName;
    link.current.href = href;

    link.current.click();
  };

  return (
    <Link {...other} ref={link} onClick={handleAction}>
      {children}
    </Link>
  );
}
